package gr.tech.site.data

/**
 * Icon token for each rebate category.
 * The actual FA composable is resolved in RebateTypeCard.kt — keeping this file pure data.
 */
enum class RebateIcon {
    Lighting,
    Hvac,
    Dehu,
    Greenhouse,
    Irrigation,
    EvChargers,
    ShadeInsulation,
    Processing,
    Other
}

data class RebateType(
    val name: String,
    val defaultDescription: String,
    val icon: RebateIcon,
    val overrideDescription: String = ""
) {
    fun descriptionFor(stateName: String): String =
        overrideDescription.ifBlank { defaultDescription }.replace("{state}", stateName)
}

/**
 * Pre-built RebateType instances for every supported category.
 * State pages import whichever ones they offer and pass them to RebateTypeGrid.
 */
object RebateTypes {

    val Lighting = RebateType(
        name = "LED Grow Light Rebates",
        defaultDescription = "DLC-qualified LED horticulture lighting qualifies for strong {state} utility rebates, helping cultivators and greenhouse operators recover a significant share of retrofit costs.",
        icon = RebateIcon.Lighting
    )

    val Hvac = RebateType(
        name = "Commercial HVAC Incentives",
        defaultDescription = "{state} utility HVAC rebate programs help offset equipment and installation costs for licensed cannabis and horticulture cultivation facilities.",
        icon = RebateIcon.Hvac
    )

    val Dehu = RebateType(
        name = "Dehumidification Rebates",
        defaultDescription = "High-humidity grow environments are prime candidates for dehumidification incentives. Cut energy consumption and operating costs at your {state} grow facility.",
        icon = RebateIcon.Dehu
    )

    val Greenhouse = RebateType(
        name = "Greenhouse & Custom Programs",
        defaultDescription = "{state} utilities offer custom incentive programs for large indoor cannabis grows and commercial greenhouse operations requiring tailored energy efficiency solutions.",
        icon = RebateIcon.Greenhouse
    )

    val Irrigation = RebateType(
        name = "Irrigation Efficiency Rebates",
        defaultDescription = "{state} utilities and water districts offer rebates for high-efficiency irrigation equipment, helping agricultural and horticultural operations reduce water and energy costs.",
        icon = RebateIcon.Irrigation
    )

    val EvChargers = RebateType(
        name = "EV Charger Incentives",
        defaultDescription = "{state} utility programs and state incentives support EV charging infrastructure for commercial agricultural facilities and grow operations.",
        icon = RebateIcon.EvChargers
    )

    val ShadeInsulation = RebateType(
        name = "Shade & Insulation Rebates",
        defaultDescription = "Thermal curtains, shade systems, and insulation upgrades qualify for {state} utility rebates that reduce heating and cooling loads in commercial grow and greenhouse facilities.",
        icon = RebateIcon.ShadeInsulation
    )

    val Processing = RebateType(
        name = "Processing Equipment Incentives",
        defaultDescription = "{state} energy efficiency programs may include rebates for processing and post-harvest equipment upgrades that reduce energy consumption in commercial cannabis and horticulture operations.",
        icon = RebateIcon.Processing
    )

    val Other = RebateType(
        name = "Other Energy Incentives",
        defaultDescription = "{state} utilities offer a range of additional energy efficiency incentives that may apply to your operation. Contact Green Rebates to discover every available program.",
        icon = RebateIcon.Other
    )
}
